import { Injectable } from '@angular/core';
import { AuthService } from '@core/auth/auth.service';
import { SpinnerService } from '@shared/services/spinner.service';
import { HttpClient } from '@angular/common/http';
import { DomSanitizer } from '@angular/platform-browser';
import { finalize, map } from 'rxjs/operators';
import { Observable, Subject } from 'rxjs';
import Utils from '@shared/utils/utils';
import { saveAs as importedSaveAs } from 'file-saver';
import { environment } from '@env/environment';
import { FileUploader } from 'ng2-file-upload';
import { BsModalService } from 'ngx-bootstrap/modal';
import { ApiServer } from '@shared/apis/servers';
import { Router } from '@angular/router';
import Swal from 'sweetalert2';

@Injectable({
  providedIn: 'root'
})
export class FileService {

  uploader: FileUploader;
  uploaderResponse$ = new Subject();
  onAfterAddingFile$ = new Subject();
  private baseUrl = environment.api.backend;

  constructor(private authService: AuthService,
              protected http: HttpClient,
              protected sanitizer: DomSanitizer,
              private spinnerService: SpinnerService,
              private modalService: BsModalService,
              private apiServer: ApiServer,
              private router: Router) {
  }

  fileUrlSecure(url, params, usePost?) {
    this.spinnerService.start();

    const request = usePost
      ? this.http.post(url, params, { responseType: 'blob' })
      : this.http.get(url, { params, responseType: 'blob' });

    return request.pipe(
      finalize(() => this.spinnerService.stop()),
      // safe bypass: the value is always a blob: URL minted here from the
      // HTTP response body — no request-controlled string reaches the DOM
      map(blob => this.sanitizer.bypassSecurityTrustUrl(URL.createObjectURL(blob)))
    );
  }

  downloadFile = (file, filename, params?, isPost?) => {
    const usePost = isPost === true;
    this.fileUrlSecure(file, params, usePost)
      .subscribe((res: any) => {
        const downloadLink = document.createElement('a');
        downloadLink.href = res.changingThisBreaksApplicationSecurity;
        downloadLink.download = filename;

        document.body.appendChild(downloadLink);
        downloadLink.click();
        document.body.removeChild(downloadLink);
      });
  };

  saveExcelFile(observable: Observable<ArrayBuffer>, filename, contentType = null) {
    if (!filename) {
      filename = 'download';
    }
    if (filename.indexOf('.') < 0) {
      filename += '.xlsx';
    }

    observable.subscribe(res => {
      const data = new Blob([res], { type: contentType || 'application/vnd.ms-excel' });
      importedSaveAs(data, filename);
    }, err => Utils.showError(err));
  }

  savePdfFile(observable: Observable<ArrayBuffer>, filename, contentType = null) {
    if (!filename) {
      filename = 'download';
    }
    if (filename.indexOf('.') < 0) {
      filename += '.pdf';
    }

    observable.subscribe(res => {
      const data = new Blob([res], { type: contentType || 'application/pdf' });
      importedSaveAs(data, filename);
    }, err => Utils.showError(err));
  }

  excelUploader(templateId: string, isSync?, opt: any = {}) {
    const url = `${ this.baseUrl }/upload/${ templateId }`;
    this.setFileUploader(url, isSync, opt);
  }

  setFileUploader(url: string, isSync?, opt: any = {}) {
    this.uploader = new FileUploader({
      url,
      autoUpload: false,
      method: 'POST',
      removeAfterUpload: true
    });

    this.uploader.onBuildItemForm = (fileItem: any, form: any) => {
      form.append('isSync', isSync);
    };

    if (!Utils.isNullObj(opt)) {
      this.uploader.onBuildItemForm = (fileItem: any, form: any) => {
        form.append('data', JSON.stringify(opt));
      };
    }

    const token = this.authService.getToken()?.token;
    const currentCompany = this.authService.getCurrentCompany();

    this.uploader.onAfterAddingFile = file => {
      file.headers = [
        { name: 'Authorization', value: `Bearer ${ token.access_token }` },
        { name: 'Company', value: currentCompany },
      ];
      file.withCredentials = true;
      this.onAfterAddingFile$.next(true);
    };

    this.uploader.onProgressItem = (progress: any) => {
      this.spinnerService.start();
    };

    this.uploader.response.subscribe(response => {
      const res = JSON.parse(response);

      this.spinnerService.stop();
      if (res.status === 'queued') {
        Swal.fire({
          title: 'Update Tracking Status',
          text: 'Result of data import will be sent to your email. Please check email and refresh page again.',
          icon: 'success',
          allowOutsideClick: true,
        });
      } else {
        Swal.fire({
          title: 'Update Tracking Status',
          text: res.message,
          icon: 'error',
          allowOutsideClick: true,
        });
      }
    });

    // TODO Check
    this.uploader.onErrorItem = (item, response, status, headers) => {
      const res = JSON.parse(response);

      // let message = res.message;
      if (res.status === 401) {
        this.router.navigate(['/']);
      }
      // if (res.statusCode === 400 && res.message) {
      //   message = res.message;
      // }
      // Utils.showMessageDialog(this.modalService, message);
    };
  }

  getExcelTemplate(templateId: string, queryString?: any) {
    this.saveExcelFile(
      this.apiServer.master.getTemplate(templateId),
      templateId,
    );
  }

  getUserGuide(fileName) {
    this.savePdfFile(
      this.apiServer.b2bTicket.getUserGuide(),
      fileName,
    );
  }

}
